import { TStatusBadgeProps } from '@treezor/design-system';

import { IUser } from '@/features/user/store/user.model';
import { IDocumentLink } from '@/store/models/shared.model';
import { TInitialFlow, TOperationsZones } from '@/store/models/operation.model';

export const walletTypeIdList = [9, 10, 15, 13, 14, 18] as const;

export type TWalletTypeId = (typeof walletTypeIdList)[number];

export const walletTypeList: Record<TWalletTypeId, string> = {
  9: 'ELECTRONIC_MONEY_WALLET',
  10: 'PAYMENT_ACCOUNT_WALLET',
  13: 'MIRROR_WALLET',
  14: 'MASTERCARD_TRANSACTION_WALLET',
  15: 'MASTER_WALLET',
  18: 'VISA_TRANSACTION_WALLET',
};

export function isTWalletTypeId(typeId: number): typeId is TWalletTypeId {
  return walletTypeIdList.includes(typeId as TWalletTypeId);
}

export const walletSortAvailableKeys = [] as const;
type TWalletSortAvailableKeys = (typeof walletSortAvailableKeys)[number];

export type TWalletSortAvailableValues = keyof Pick<IWallet, Exclude<TWalletSortAvailableKeys, 'id'>> | 'id';
export function isWalletsSortKeyAvailable(key: string): key is TWalletSortAvailableKeys {
  return key === 'id' || walletSortAvailableKeys.includes(key as TWalletSortAvailableKeys);
}

export interface ISearchWallet {
  userId?: number;
  walletId?: number;
  pageNumber?: number;
  pageCount?: number;
  walletStatus?: keyof typeof walletStatusList;
  walletTypeId?: number;
  walletTypeIdList?: string;
  tariffId?: number;
  sortBy?: TWalletSortAvailableValues;
  sortOrder?: 'ASC' | 'DESC';
  createdDateFrom?: string;
  createdDateTo?: string;
}

export const walletStatusList = {
  VALIDATED: 'VALIDATED',
  PENDING: 'PENDING',
  CANCELED: 'CANCELED',
} as const;

export type TWalletStatus = keyof typeof walletStatusList;

export const walletStatusColors: Record<keyof typeof walletStatusList, TStatusBadgeProps['color']> = {
  VALIDATED: 'green',
  PENDING: 'orange',
  CANCELED: 'red',
};

export interface IWallet {
  walletId: number;
  walletTypeId?: number;
  walletStatus?: TWalletStatus;
  codeStatus?: number;
  informationStatus?: string;
  walletTag?: string;
  userId: number;
  userLastname?: string;
  userFirstname?: string;
  jointUserId?: number;
  tariffId?: number;
  eventName?: string;
  eventAlias?: string;
  eventDate?: string;
  eventMessage?: string;
  eventPayinStartDate?: string;
  eventPayinEndDate?: string;
  contractSigned?: number;
  bic?: string;
  iban?: string;
  urlImage?: string;
  currency?: string;
  createdDate?: string;
  modifiedDate?: string;
  payinCount?: number;
  payoutCount?: number;
  transferCount?: number;
  solde?: number;
  authorizedBalance?: number;
  totalRows?: number;
  country?: string;
}

export function isWallet(maybe: any): maybe is IWallet {
  return (maybe as IWallet).walletId !== undefined && (maybe as IWallet).authorizedBalance !== undefined;
}

export interface IWalletStatementDownload extends IDocumentLink {}

export interface IWalletAccountDetailsDownload extends IDocumentLink {}

export interface IWalletDomiciliationCertificateDownload extends IDocumentLink {}

export interface IWalletBalanceCertificateDownload extends IDocumentLink {}

export interface IWalletClosureCertficateDownload extends IDocumentLink {}

export interface IOperationProofOfPayoutDownload extends IDocumentLink {}

export const walletOwnerTypes = {
  DEBTOR: 'debtor',
  CREDITOR: 'creditor',
} as const;

export type TWAlletOwnerTypes = (typeof walletOwnerTypes)[keyof typeof walletOwnerTypes];

export interface IWalletDebtor {
  debtorLegalName: string;
  debtorFirstName: string;
  debtorLastName: string;
  debtorWalletEventName: string;
  debtorWalletId: string;
}

export interface IWalletCreditor {
  creditorLegalName: string;
  creditorFirstName: string;
  creditorLastName: string;
  creditorWalletEventName: string;
  creditorWalletId: string;
}

export interface IWalletCreation {
  walletTypeId: number;
  tariffId: number;
  userId: number;
  currency: string;
  eventName: string;
  jointUserId: number;
  walletTag: string;
  eventAlias?: string;
  eventMessage?: string;
}

export const WalletTypeForBulkCardCreation = {
  10: 'Payment',
  9: 'e-Money',
};

export interface IWalletType {
  label: string;
  value: number;
}

export const defaultWalletTypes: IWalletType[] = [
  { label: 'e-Money', value: 9 },
  { label: 'Payment', value: 10 },
  { label: 'Technical', value: 15 },
  { label: 'Mirror', value: 13 },
  { label: 'Electronic Money Card', value: 14 },
];

export interface IDataBlock<T> {
  user?: IUser;
  wallet?: IWallet;
  header: string;
  data: T;
  isEditable?: boolean;
}

export interface IOperationsColumns {
  /* eslint-disable @typescript-eslint/naming-convention */
  creation: boolean;
  settlement: boolean;
  operationType: boolean;
  label: boolean;
  status: boolean;
  amount: boolean;
  emitterId: boolean;
  receiverId: boolean;
  tag: boolean;
  authorizationNote: boolean;
  localAmount: boolean;
  localAmountCurrency: boolean;
  MidValue: boolean;
  MccCode: boolean;
  action: boolean;
  /* eslint-enable @typescript-eslint/naming-convention */
}

export const displayvalues = {
  ALL: 'all',
  CRYPTO_ONLY: 'crypto',
  NO_CRYPTO: '!crypto',
  NONE: 'none',
} as const;

export type TDisplayValues = (typeof displayvalues)[keyof typeof displayvalues];

export interface IAttributeExtended<T extends IAttributeConfig> extends IAttributeConfig {
  childs: T[];
}

export const checkedValues = { TRUE: true, FALSE: false, INDETERMINATE: 'indeterminate' } as const;
export type TCheckedValues = (typeof checkedValues)[keyof typeof checkedValues];

export interface IAttributeConfig {
  display: TDisplayValues;
  name: string;
  types?: any[];
  initialFlows?: TInitialFlow[];
  childs?: IAttributeConfig[];
  defaultValue?: string | boolean;
  value?: string | boolean;
  type?: 'list';
  isChecked?: TCheckedValues;
}

export interface IAttributesContext {
  type?: string;
  initialFlow?: TInitialFlow;
  zone?: TOperationsZones;
  isCrypto?: boolean;
  fields?: Record<string, boolean>;
  select?: string[];
  setChecked?: boolean | ((attribute: IAttributeConfig) => boolean);
}
export type TAttributesContextWithChecked = IAttributesContext & {
  setChecked: boolean | ((attribute: IAttributeConfig) => boolean);
};
